/**
 * Orchestrator — sequences multi-step agent work end-to-end under governance.
 *
 * This is the piece that turns Argo from single-shot assisted data-entry into
 * an agentic OS: a Goal is decomposed into a durable, resumable plan of steps,
 * and the run walks the plan — executing read steps automatically and routing
 * every write step through the SAME approvals gate as everything else. The
 * Orchestrator can never bypass governance; it only sequences it.
 *
 * Decision Ledger: the run object (goal + plan + per-step outcome) plus the
 * append-only `activity` log together are the auditable record of what the
 * system decided and did. Write steps join to the ledger via `approvalId`
 * (activity entries carry it as `subjectId`), so there is no parallel log.
 *
 * HONEST about intelligence: Phase A decomposes goals with deterministic
 * TEMPLATES (a goal → a known step sequence built from real store data). No
 * LLM is claimed. Free-form goal decomposition via the Claude worker is Phase B.
 *
 * Resume model: `advanceRun` is idempotent and reads live approval state, so
 * calling it again after a gated step is approved continues the run. Callers
 * (the Goals UI) re-drive it when runs or approvals change; it also survives
 * reload because the run is persisted.
 */

import { db, nowIso, uuid } from '../db/store';
import { scanSystem } from './systemScanner';
import { rankCustomersByRisk, type HealthBand } from '../crm/health';
import { checkinTaskFor } from './customerHealthAgent';
import { queueApproval, listApprovals, resolveApproval } from './approvals';
import { listActivity } from '../db/activity';
import type { OrchestratorRun, PlanStep } from '../db/types';
import type { CreateTaskAction, ScheduleFollowUpAction } from './types';

/** Default blast-radius cap: max write steps a single run may execute. */
export const DEFAULT_MAX_WRITES = 10;

const TERMINAL: ReadonlySet<PlanStep['status']> = new Set(['done', 'skipped', 'failed']);

/** A deterministic goal → plan decomposition. */
export interface GoalTemplate {
  id: string;
  label: string;
  description: string;
  /** Read the store and return the plan's steps (read steps pre-resolved). */
  plan: () => Promise<PlanStep[]>;
}

// ---- Template 1: clear the overdue follow-up backlog ----------------------
const FOLLOWUP_CATEGORIES = new Set(['Overdue follow-up', 'No next step']);

/**
 * Shared scan + step-builder for the drifting-prospect follow-up work.
 * De-dupes against follow-ups already awaiting approval so a run doesn't
 * pile duplicates for a prospect the operator is already looking at.
 */
async function planDriftingFollowUps(): Promise<{
  scanned: number;
  drifting: number;
  writes: PlanStep[];
}> {
  const people = await db.list('people');
  const { findings } = scanSystem({ tickets: [], tasks: [], people, accounts: [] });
  const drifting = findings.filter(
    (f) => f.source === 'prospect' && FOLLOWUP_CATEGORIES.has(f.category),
  );
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'schedule_follow_up')
      .map((a) => (a.action as ScheduleFollowUpAction).personId),
  );

  const writes: PlanStep[] = [];
  for (const f of drifting) {
    const person = people.find((p) => p.id === f.entityId);
    if (!person || alreadyQueued.has(person.id)) continue;
    alreadyQueued.add(person.id);
    const action: ScheduleFollowUpAction = {
      kind: 'schedule_follow_up',
      personId: person.id,
      personName: person.name,
      note:
        f.category === 'Overdue follow-up'
          ? 'Re-engage — the scheduled follow-up has lapsed.'
          : 'Set a next step so this open deal stops drifting.',
    };
    writes.push({
      id: uuid(),
      kind: 'write',
      description: `Schedule follow-up — ${person.name}`,
      status: 'pending',
      action,
    });
  }
  return { scanned: people.length, drifting: drifting.length, writes };
}

const clearOverdueFollowups: GoalTemplate = {
  id: 'clear-overdue-followups',
  label: 'Clear overdue follow-ups',
  description: 'Find every drifting prospect and propose a follow-up for each.',
  async plan() {
    const { scanned, drifting, writes } = await planDriftingFollowUps();
    return [
      {
        id: uuid(),
        kind: 'read',
        description: `Scan prospects for drift`,
        status: 'done',
        result: `${scanned} prospects scanned · ${drifting} drifting`,
      },
      ...writes,
    ];
  },
};

// ---- Template 2: re-engage at-risk customers -------------------------------
// Same bands the customer-success agent nudges on: watch + at-risk.
const BANDS_NEEDING_CHECKIN: ReadonlySet<HealthBand> = new Set(['watch', 'at-risk']);

/**
 * Shared scan + step-builder for the at-risk customer check-in work.
 * De-dupes by check-in title against pending approvals so a run doesn't
 * pile a duplicate for an account the health agent already proposed on.
 */
async function planAtRiskCheckins(): Promise<{
  scanned: number;
  slipping: number;
  writes: PlanStep[];
}> {
  const [people, tasks] = await Promise.all([db.list('people'), db.list('tasks')]);
  const ranked = rankCustomersByRisk(people, { tasks });
  const slipping = ranked.filter((r) => BANDS_NEEDING_CHECKIN.has(r.health.band));
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'create_task')
      .map((a) => (a.action as CreateTaskAction).title),
  );

  const writes: PlanStep[] = [];
  for (const { person, health } of slipping) {
    const action = checkinTaskFor(person, health.band);
    if (alreadyQueued.has(action.title)) continue;
    alreadyQueued.add(action.title);
    // Name the band + top risk factor so the gate explains itself.
    const topFactor = health.factors.find((f) => f.points < 0);
    writes.push({
      id: uuid(),
      kind: 'write',
      description: `${action.title} · Health ${health.score}/100 (${health.band})${
        topFactor ? ` · ${topFactor.label}` : ''
      }`,
      status: 'pending',
      action,
    });
  }
  return { scanned: ranked.length, slipping: slipping.length, writes };
}

const reEngageAtRisk: GoalTemplate = {
  id: 're-engage-at-risk',
  label: 'Re-engage at-risk customers',
  description: 'Score every live customer and propose a check-in for each slipping account.',
  async plan() {
    const { scanned, slipping, writes } = await planAtRiskCheckins();
    return [
      {
        id: uuid(),
        kind: 'read',
        description: `Scan customers for health risk`,
        status: 'done',
        result: `${scanned} customers scanned · ${slipping} need a check-in`,
      },
      ...writes,
    ];
  },
};

// ---- Template 3: the Monday brief ------------------------------------------
// Composes templates 1 + 2 into one combined run: a single read step that
// summarizes both scans, then the follow-up writes, then the check-in writes.
const mondayBrief: GoalTemplate = {
  id: 'monday-brief',
  label: 'Prep the Monday brief',
  description:
    'Scan prospects for drift and customers for health risk, then propose the full week-start action list in one governed run.',
  async plan() {
    const prospects = await planDriftingFollowUps();
    const customers = await planAtRiskCheckins();
    return [
      {
        id: uuid(),
        kind: 'read',
        description: `Scan prospects for drift and customers for health risk`,
        status: 'done',
        result: `${prospects.scanned} prospects · ${prospects.drifting} drifting · ${customers.scanned} customers · ${customers.slipping} need a check-in`,
      },
      ...prospects.writes,
      ...customers.writes,
    ];
  },
};

/** The Phase-A goal template registry. */
export const GOAL_TEMPLATES: GoalTemplate[] = [clearOverdueFollowups, reEngageAtRisk, mondayBrief];

export function getTemplate(id: string): GoalTemplate | undefined {
  return GOAL_TEMPLATES.find((t) => t.id === id);
}

// ---- Persistence helpers ---------------------------------------------------
async function save(run: OrchestratorRun): Promise<OrchestratorRun> {
  const updated = { ...run, updatedAt: nowIso() };
  await db.update('orchestratorRuns', run.id, {
    status: updated.status,
    steps: updated.steps,
    updatedAt: updated.updatedAt,
  });
  return updated;
}

export async function getRun(id: string): Promise<OrchestratorRun | null> {
  return db.get('orchestratorRuns', id);
}

export async function listRuns(): Promise<OrchestratorRun[]> {
  const all = await db.list('orchestratorRuns');
  return [...all].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

export function subscribeRuns(fn: (runs: OrchestratorRun[]) => void): () => void {
  return db.subscribe('orchestratorRuns', (rows) =>
    fn([...rows].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))),
  );
}

// ---- The state machine -----------------------------------------------------

/**
 * Start a run for a goal template: decompose into a plan, persist, and advance
 * to the first gate. Returns the run (typically already `awaiting_approval` on
 * its first write step, or `done` if the goal needed no writes).
 */
export async function startRun(goalId: string): Promise<OrchestratorRun> {
  const template = getTemplate(goalId);
  if (!template) throw new Error(`Unknown goal template: ${goalId}`);
  const steps = await template.plan();
  const ts = nowIso();
  const run: OrchestratorRun = {
    id: uuid(),
    goalId: template.id,
    goalLabel: template.label,
    status: 'running',
    steps,
    maxWrites: DEFAULT_MAX_WRITES,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('orchestratorRuns', run);
  return advanceRun(run.id);
}

/**
 * Advance a run as far as it can go: fast-forward read steps and already
 * -resolved write steps, then either queue the next pending write for approval
 * (and pause) or, if a write is still awaiting its approval, pause. Idempotent
 * — safe to call repeatedly (e.g. whenever approvals change). Enforces the
 * blast-radius cap: once `maxWrites` executions land, the run pauses.
 */
export async function advanceRun(id: string): Promise<OrchestratorRun> {
  let run = await db.get('orchestratorRuns', id);
  if (!run) throw new Error(`No run ${id}`);
  if (run.status === 'done' || run.status === 'cancelled') return run;

  const steps = run.steps.map((s) => ({ ...s }));
  let executed = steps.filter((s) => s.kind === 'write' && s.status === 'done').length;

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (TERMINAL.has(step.status)) continue;

    if (step.kind === 'read') {
      step.status = 'done';
      continue;
    }

    // write step
    if (step.status === 'awaiting_approval' && step.approvalId) {
      const appr = await db.get('approvals', step.approvalId);
      if (!appr || appr.status === 'pending') {
        return save({ ...run, status: 'awaiting_approval', steps });
      }
      if (appr.status === 'approved') {
        step.status = 'done';
        step.result = 'Approved & executed';
        executed++;
        continue;
      }
      step.status = 'skipped';
      step.result = 'Dismissed';
      continue;
    }

    if (step.status === 'pending') {
      if (executed >= run.maxWrites) {
        // Blast-radius reached — pause rather than queue more without a fresh
        // operator decision. (A future "raise cap / continue" control resumes.)
        return save({ ...run, status: 'awaiting_approval', steps });
      }
      const appr = await queueApproval(step.action!, 'scheduler', step.description);
      step.approvalId = appr.id;
      step.status = 'awaiting_approval';
      return save({ ...run, status: 'awaiting_approval', steps });
    }
  }

  // Nothing left to do.
  const allTerminal = steps.every((s) => TERMINAL.has(s.status));
  return save({ ...run, status: allTerminal ? 'done' : run.status, steps });
}

/**
 * Plan-level batch approval: drive a whole run to completion in one call —
 * the backend for an "Approve plan" button. This is the human approving the
 * ENTIRE plan at once, not a governance bypass: every write step still flows
 * through the exact same path as a manual per-gate approval — `advanceRun`
 * queues it via `queueApproval`, we resolve it 'approved' (which executes the
 * action and records proposed → approved → executed in the activity ledger),
 * and `advanceRun` marks it done. Read steps fast-forward as usual, the
 * blast-radius cap still pauses the run, and dismissed gates still skip.
 *
 * Failure honesty: `resolveApproval` deliberately never logs 'executed' for a
 * commit that didn't happen, so we treat a missing 'executed' ledger entry as
 * execution failure — the step is marked 'failed' and we stop immediately,
 * leaving the rest of the plan untouched and the run resumable (a later
 * `advanceRun`/`approveRun` continues past the failed step). ADDITIVE: this
 * reuses `advanceRun` as the single state machine; the one-gate-at-a-time
 * path is unchanged.
 */
export async function approveRun(id: string): Promise<OrchestratorRun> {
  let run = await advanceRun(id);
  // Each iteration resolves at most one gate, so the plan length bounds the
  // loop — belt-and-braces against a stuck run looping forever.
  for (let guard = run.steps.length + 1; guard > 0 && run.status === 'awaiting_approval'; guard--) {
    const gate = run.steps.find((s) => s.status === 'awaiting_approval' && s.approvalId);
    // Paused for another reason (blast-radius cap) — that pause is a real
    // operator decision point, so batch approval stops there too.
    if (!gate) return run;

    const appr = await db.get('approvals', gate.approvalId!);
    if (appr && appr.status === 'pending') {
      // The SAME call a manual approval makes: executes the action and logs
      // 'approved' + 'executed' to the ledger with the approval as subject.
      await resolveApproval(gate.approvalId!, 'approved');
      const ledger = await listActivity();
      const committed = ledger.some(
        (e) => e.event === 'executed' && e.subjectId === gate.approvalId,
      );
      if (!committed) {
        // Execution failed inside resolveApproval (which records the approval
        // intent but never claims work that didn't land). Mark the step failed
        // and stop — 'running' keeps the run resumable, never silently broken.
        const steps = run.steps.map((s) =>
          s.id === gate.id
            ? { ...s, status: 'failed' as const, result: 'Approved, but execution failed' }
            : { ...s },
        );
        return save({ ...run, status: 'running', steps });
      }
    }
    // Fold the resolved gate into the run and advance to the next one.
    run = await advanceRun(id);
  }
  return run;
}

/**
 * Sweep every in-flight run forward. Called on the scheduler tick so a run
 * whose gated approval was resolved resumes autonomously — no UI needed.
 * Best-effort and idempotent: `advanceRun` no-ops while a gate is still
 * pending, and one bad run must not break the sweep, so failures are
 * swallowed per-run.
 */
export async function advancePausedRuns(): Promise<void> {
  const runs = await listRuns();
  for (const run of runs) {
    if (run.status !== 'awaiting_approval' && run.status !== 'running') continue;
    try {
      await advanceRun(run.id);
    } catch {
      /* best-effort — skip the bad run and keep sweeping */
    }
  }
}

/** Cancel a run. Already-executed steps stand (and remain in the ledger). */
export async function cancelRun(id: string): Promise<OrchestratorRun> {
  const run = await db.get('orchestratorRuns', id);
  if (!run) throw new Error(`No run ${id}`);
  return save({ ...run, status: 'cancelled' });
}
